import {useCallback, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useDebouncedCallback} from 'use-debounce';
import {isSlideModifiedSelector, selectedSlideSelector} from '../../../store/selectors/slide.selectors';
import useSaveSlide from './useSaveSlide';
import {loggedInUserSelector} from '../../../store/selectors/auth.selectors';

export function useOnSlideModified(): {
    cancelAutosave: () => void;
    suppressAutosave: () => void;
    allowAutosave: () => void;
} {
    const dispatch = useDispatch();
    const user = useSelector(loggedInUserSelector);
    const slide = useSelector(selectedSlideSelector);
    const modified = useSelector(isSlideModifiedSelector);
    const slideMutation = useSaveSlide({omitToastMessage: true});
    const mutateSlideDebounced = useDebouncedCallback(slideMutation.mutate, 5000);
    const suppressAutosaveRef = useRef(false);

    const cancelAutosave = useCallback(() => {
        mutateSlideDebounced.cancel();
    }, [mutateSlideDebounced]);

    const suppressAutosave = useCallback(() => {
        mutateSlideDebounced.cancel();
        suppressAutosaveRef.current = true;
    }, [mutateSlideDebounced]);

    const allowAutosave = useCallback(() => {
        suppressAutosaveRef.current = false;
    }, []);

    useEffect(() => {
        if (!modified) {
            suppressAutosaveRef.current = false;
            return;
        }

        if (user?.autoSave && !suppressAutosaveRef.current) {
            mutateSlideDebounced({slide});
        }
    }, [dispatch, modified, mutateSlideDebounced, slide, user?.autoSave]);

    return {cancelAutosave, suppressAutosave, allowAutosave};
}
